import {
    checkoutConfirmLabelKey,
    SubscriptionCheckoutState,
    type SubscriptionCheckoutOffer,
} from '@houzy/schemas/partner-management/partner-subscription-management';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorMessageKey } from '../../../lib/api';
import { PAYMENT_METHOD_PRESENTATION } from '../../../lib/paymentMethodPresentation';
import { formatSwissDate } from '../../../lib/time';
import { useTrackEvent } from '../../../lib/trackingEvents';
import { checkoutQueryOptions, useConfirmCheckout } from '../api/subscriptionCheckoutApi';
import { useTrackCheckoutCompleted, useTrackCheckoutLoaded } from '../checkoutTracking';
import { CheckoutContact } from '../components/CheckoutContact';
import { CheckoutPaymentMethodCard } from '../components/CheckoutPaymentMethodCard';
import { CheckoutPlanCard } from '../components/CheckoutPlanCard';
import { CheckoutPricingCard } from '../components/CheckoutPricingCard';
import { CheckoutTermsConsent } from '../components/CheckoutTermsConsent';
import { CheckoutConfirmed, CheckoutExpired, CheckoutSuperseded, CheckoutUnavailable } from './CheckoutStatePages';

export type SubscriptionCheckoutPageProps = {
    token: string;
    payment?: 'success' | 'cancelled';
};

// Stripe returns the partner before its webhook has necessarily reached us, so someone who really did finish can
// land on a page that still says otherwise. We poll at 3 seconds for 15.
const WEBHOOK_POLL_INTERVAL_MS = 3_000;
const WEBHOOK_POLL_WINDOW_MS = 15_000;

// In case of the offer changing in the ops-tool.
const OFFER_REFRESH_INTERVAL_MS = 60_000;

export function SubscriptionCheckoutPage({ token, payment }: SubscriptionCheckoutPageProps): ReactElement {
    const pollUntil = useRef(Date.now() + WEBHOOK_POLL_WINDOW_MS);
    const { data: view } = useSuspenseQuery({
        ...checkoutQueryOptions(token),
        // Coming back to the tab is the likeliest moment for the offer to have moved on underneath it.
        refetchOnWindowFocus: true,
        refetchInterval: (query): number | false => {
            const state = query.state.data?.state;
            if (state === SubscriptionCheckoutState.AwaitingConfirmation) {
                return OFFER_REFRESH_INTERVAL_MS;
            }
            const awaiting = state === SubscriptionCheckoutState.AwaitingPaymentDetails;
            if (payment !== 'success' || !awaiting) {
                return false;
            }
            // Kept a pure derivation: React Query resolves this from every observer update rather than once per
            // elapsed interval, so a countdown here would be spent on state changes instead of on polls.
            return Date.now() > pollUntil.current ? OFFER_REFRESH_INTERVAL_MS : WEBHOOK_POLL_INTERVAL_MS;
        },
    });

    useTrackCheckoutCompleted({
        source: 'ops-link',
        hasOrder: 'plan' in view,
        isConfirmed: view.state === SubscriptionCheckoutState.ConfirmedByPartner,
        payment,
    });

    switch (view.state) {
        case SubscriptionCheckoutState.Superseded:
            return <CheckoutSuperseded />;
        case SubscriptionCheckoutState.Expired:
            return <CheckoutExpired />;
        case SubscriptionCheckoutState.Unavailable:
            return <CheckoutUnavailable />;
        case SubscriptionCheckoutState.ConfirmedByPartner:
            return <CheckoutConfirmed offer={view} />;
        default:
            return <CheckoutForm offer={view} token={token} payment={payment} />;
    }
}

function CheckoutForm({
    offer,
    token,
    payment,
}: {
    offer: SubscriptionCheckoutOffer;
    token: string;
    payment?: 'success' | 'cancelled';
}): ReactElement {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [accepted, setAccepted] = useState(false);
    const [showConsentError, setShowConsentError] = useState(false);
    const confirm = useConfirmCheckout(token);
    const track = useTrackEvent();

    const isRetry = offer.state === SubscriptionCheckoutState.AwaitingPaymentDetails;
    useTrackCheckoutLoaded({ source: 'ops-link', payment });
    // Stripe can return the partner before the corresponding webhook event is delivered.
    const awaitingWebhook = isRetry && payment === 'success';

    const submit = (): void => {
        if (!accepted) {
            setShowConsentError(true);
            return;
        }
        confirm.mutate(undefined, {
            onSuccess: async ({ redirectUrl }) => {
                track('Checkout Submitted', { checkoutSource: 'ops-link' });
                if (redirectUrl) {
                    window.location.assign(redirectUrl);
                    return;
                }
                await queryClient.invalidateQueries({ queryKey: checkoutQueryOptions(token).queryKey });
            },
        });
    };

    const ctaKey = checkoutConfirmLabelKey(offer);

    return (
        <Stack spacing={4}>
            <Stack spacing={1}>
                <Typography variant="h4">{t('CHECKOUT.HEADING')}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('CHECKOUT.SUBHEADING', { name: offer.greetingName })}
                </Typography>
            </Stack>

            {isRetry &&
                (payment === 'success' ? (
                    <Alert severity="info">{t('CHECKOUT.CONFIRMING_NOTICE')}</Alert>
                ) : (
                    <Alert severity="warning">{t('CHECKOUT.RETRY_NOTICE')}</Alert>
                ))}

            <Stack spacing={2}>
                <CheckoutPlanCard plan={offer.plan} agreedStartAt={offer.agreedStartAt} />
                <CheckoutPricingCard offer={offer} />
                <CheckoutPaymentMethodCard offer={offer} />

                <Stack spacing={1}>
                    <CheckoutTermsConsent
                        commitmentLengthInMonths={offer.plan.commitmentLengthInMonths}
                        autoProlongation={offer.autoProlongation}
                        accepted={accepted}
                        onAcceptedChange={(next) => {
                            setAccepted(next);
                            if (next) {
                                setShowConsentError(false);
                            }
                        }}
                        showError={showConsentError}
                    />

                    {confirm.isError && (
                        <Alert severity="error">
                            {t(apiErrorMessageKey(confirm.error, 'CHECKOUT.ERRORS.CONFIRM_FAILED'))}
                        </Alert>
                    )}

                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        loading={confirm.isPending || awaitingWebhook}
                        disabled={awaitingWebhook}
                        startIcon={PAYMENT_METHOD_PRESENTATION[offer.paymentMethod].icon}
                        onClick={submit}>
                        {t(ctaKey)}
                    </Button>
                </Stack>

                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                    <LockOutlinedIcon sx={{ fontSize: 12, color: 'action.active', flexShrink: 0 }} />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center', fontWeight: 500, fontSize: 11, letterSpacing: '0.3px' }}>
                        {t('CHECKOUT.ENCRYPTED')}
                        {offer.cancellableUntil &&
                            ` ${t('CHECKOUT.CANCEL_BEFORE', { date: formatSwissDate(new Date(offer.cancellableUntil)) })}`}
                    </Typography>
                </Stack>
            </Stack>

            <Divider />
            <CheckoutContact />
        </Stack>
    );
}
